import React,{useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {HashLink as Link} from 'react-router-hash-link';
import {Helmet} from 'react-helmet';
import {CheckIcon,XIcon} from '@heroicons/react/outline';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faHeart,faGem} from '@fortawesome/free-regular-svg-icons';
import {faFeather,faCrown} from '@fortawesome/free-solid-svg-icons';
import {sprintf} from 'sprintf-js';
import sendReqUtil from '../../utils/send-req-util';
import locales from '../../locales/locales';
import engineMap from '../../constants/engine-map';
import plans from '../../constants/plans';
import Navbar from '../navbar/navbar';
import CTA from '../cta/cta';
import Footer from '../footer/footer';
const Pricing=({globalSettings})=>{
  const [popupLevel,setPopupLevel]=useState(null);
  const navigate=useNavigate();
  const iconMap={faHeart,faFeather,faGem,faCrown};
  const handlePlanClick=(level)=>{
    if(globalSettings.user){
      localStorage.setItem('intendedNextLevel',level);
      if(globalSettings.user.subs_id){
        setPopupLevel(level);
      }
      else{
        Paddle.Checkout.open({
          settings:{
            allowLogout:false,
            successUrl:`${window.location.origin}/${globalSettings.lang}/payment-success`,
          },
          customer:{
            email: globalSettings.user.email,
          },
          items:[
            {
              priceId:plans[level].monthlyPriceId,
              quantity:1,
            },
          ],
        });
      }
    }
    else{
      navigate(`/${globalSettings.lang}/login`);
    }
  };
  const handlePlanChange=(e)=>{
    e.preventDefault();
    sendReqUtil('/change-plan',{
      'new_level':popupLevel,
    });
    setPopupLevel(null);
    navigate(`/${globalSettings.lang}/payment-success`);
  };
  return (
    <div className='min-h-screen flex flex-col bg-white'>
      <Helmet>
        <title>{`NekoTranslate - ${locales.pricingTitle[globalSettings.lang]}`}</title>
      </Helmet>
      <Navbar
        globalSettings={globalSettings}
      />
      <div className='pt-16 bg-purple-50'>
        <div className='w-full px-4 sm:px-6 lg:px-8 py-16 md:py-24'>
          <div className='text-center mb-16'>
            <h1 className='text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight'>
              {locales.pricingHeadingPrefix[globalSettings.lang]}
              <span className='text-purple-800'>
                {locales.pricingHeadingHighlight[globalSettings.lang]}
              </span>
              {locales.pricingHeadingSuffix[globalSettings.lang]}
            </h1>
            <p className='mt-4 text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto'>
              {locales.pricingSubtitle[globalSettings.lang]}
            </p>
          </div>
          <div className='w-full flex justify-center px-4 sm:px-6 lg:px-8'>
            <div className='grid grid-cols-1 min-[835px]:grid-cols-2 xl:grid-cols-4 gap-6'>
              {plans.map((plan,index)=>(
                <div 
                  key={plan.name} 
                  className={`bg-white p-6 rounded-lg shadow-lg border border-gray-200 hover:shadow-xl hover:border-purple-300 transition-all duration-300 relative flex flex-col w-80 max-w-full mx-auto ${plan.name==='Standard'?'ring-2 ring-purple-500 shadow-lg':''}`}
                >
                  {plan.name==='Standard'&&
                    <div className='absolute top-0 right-0 bg-purple-800 text-white px-3 py-1.5 rounded-tr-lg rounded-bl-lg text-xs font-medium shadow-lg'>
                      {locales.pricingPopular[globalSettings.lang]}
                    </div>
                  }
                  <div className='text-center mb-5'>
                    <div className='h-14 w-14 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4'>
                      <FontAwesomeIcon 
                        icon={iconMap[plan.icon]} 
                        className='h-7 w-7 text-purple-800' 
                      />
                    </div>
                    <h3 className='text-2xl font-bold text-gray-900 mb-2'>
                      {plan.name}
                    </h3>
                    <p className='text-base text-gray-600 mb-4'>
                      {
                        plan.needsAccount?
                        locales.pricingAccountRequired[globalSettings.lang]:
                        locales.pricingAccountNotRequired[globalSettings.lang]
                      }
                    </p>
                    <div className='text-4xl font-bold text-gray-900'>
                      {
                        plan.pricePerMonth===0?
                        locales.pricingPrice0[globalSettings.lang]:
                        `$${plan.pricePerMonth}`
                      }
                      {
                        plan.pricePerMonth>0&&
                        <span className='text-xl text-gray-600 font-normal'>
                          {locales.pricingPerMonth[globalSettings.lang]}
                        </span>
                      }
                    </div>
                  </div>
                  <div className='flex-grow'>
                    <ul className='space-y-3 mt-5 mb-6'>
                      {plan.features.map((feature,i)=>(
                        <li 
                          key={i} 
                          className='flex items-start'
                        >
                          <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mr-2.5 mt-0.5 ${feature.has?'bg-green-100':'bg-red-100'}`}>
                            {feature.has?
                              <CheckIcon className='h-4 w-4 text-green-600' />:
                              <XIcon className='h-4 w-4 text-red-600' />
                            }
                          </div>
                          <span className={`text-base ${feature.has?'text-gray-700':'text-gray-500'}`}>
                            {((typeof feature.name)==='string')?feature.name:feature.name[globalSettings.lang]}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className='mt-auto'>
                    {index === 0 ? (
                      <Link 
                        to={`/${globalSettings.lang}/translator`} 
                        className='w-full bg-white border-2 border-purple-800 text-purple-800 hover:bg-purple-50 px-4 py-3 rounded-lg text-base font-medium block text-center transition-all duration-200'
                      >
                        {locales.pricingTryForFree[globalSettings.lang]}
                      </Link>
                    ) : index === globalSettings.user?.next_level ? (
                      <button 
                        disabled
                        className='w-full bg-gray-300 text-gray-500 px-4 py-3 rounded-lg text-base font-medium block text-center cursor-not-allowed'
                      >
                        {locales.pricingCurrentPlan[globalSettings.lang]}
                      </button>
                    ) : (
                      <button 
                        onClick={() => handlePlanClick(index)} 
                        className='w-full bg-purple-800 hover:bg-purple-600 text-white px-4 py-3 rounded-lg text-base font-medium block text-center transition-all duration-200'
                      >
                        {index<globalSettings.user?.next_level?
                          sprintf(locales.pricingDowngradeTo[globalSettings.lang],plan.name):
                          sprintf(locales.pricingUpgradeTo[globalSettings.lang],plan.name)
                        }
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className='py-16 bg-white'>
        <div className='w-full px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-12'>
            <h2 className='text-3xl font-bold text-gray-900'>
              {locales.pricingCreditCosts[globalSettings.lang]}
            </h2>
            <p className='mt-4 text-xl text-gray-600'>
              {locales.pricingTransparentPricing[globalSettings.lang]}
            </p>
          </div>
          <div className='bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden max-w-2xl mx-auto'>
            <div className='grid grid-cols-2 gap-0'>
              <div className='bg-purple-50 px-6 py-4 border-r border-gray-200'>
                <h3 className='text-lg font-semibold text-gray-900'>
                  {locales.pricingModel[globalSettings.lang]}
                </h3>
              </div>
              <div className='bg-purple-50 px-6 py-4'>
                <h3 className='text-lg font-semibold text-gray-900'>
                  {locales.pricingCreditsPerPage[globalSettings.lang]}
                </h3>
              </div>
              {Object.entries(engineMap)
                .map(([key, engine], index) => (
                  <React.Fragment key={key}>
                    <div className={`px-6 py-4 border-r border-gray-200 border-t border-gray-200 ${index % 2 === 1 ? 'bg-gray-50' : ''}`}>
                      <span className='font-medium text-gray-900'>{(typeof engine.name==='string')?engine.name:engine.name[globalSettings.lang]}</span>
                    </div>
                    <div className={`px-6 py-4 border-t border-gray-200 ${index % 2 === 1 ? 'bg-gray-50' : ''}`}>
                      <span className='text-purple-800 font-bold text-lg'>{engine.cpp}</span>
                    </div>
                  </React.Fragment>
                ))
              }
            </div>
          </div>
          <p className='text-center text-gray-500 mt-6'>
            {locales.pricingCreditsConsumed[globalSettings.lang]}
          </p>
          <p className='text-center text-gray-500 mt-2'>
            {locales.pricingChooseModel[globalSettings.lang]}
          </p>
        </div>
      </div>
      <div className='py-16 bg-purple-50'>
        <div className='w-full px-4 sm:px-6 lg:px-8'>
          <div className='text-center mb-12'>
            <h2 className='text-3xl font-bold text-gray-900'>
              {locales.pricingFAQ[globalSettings.lang]}
            </h2>
          </div>
          <div className='space-y-6'>
            <div className='bg-white p-6 rounded-lg border border-gray-200'>
              <h3 className='text-lg font-medium text-gray-900'>
                {locales.pricingFAQAccount[globalSettings.lang]}
              </h3>
              <p className='mt-2 text-gray-600'>
                {locales.pricingFAQAccountAnswer[globalSettings.lang]}
              </p>
            </div>
            <div className='bg-white p-6 rounded-lg border border-gray-200'>
              <h3 className='text-lg font-medium text-gray-900'>
                {locales.pricingFAQCredit[globalSettings.lang]}
              </h3>
              <p className='mt-2 text-gray-600'>
                {locales.pricingFAQCreditAnswer[globalSettings.lang]}
              </p>
            </div>
            <div className='bg-white p-6 rounded-lg border border-gray-200'>
              <h3 className='text-lg font-medium text-gray-900'>
                {locales.pricingFAQModels[globalSettings.lang]}
              </h3>
              <p className='mt-2 text-gray-600'>
                {locales.pricingFAQModelsAnswer[globalSettings.lang]}
              </p>
            </div>
            <div className='bg-white p-6 rounded-lg border border-gray-200'>
              <h3 className='text-lg font-medium text-gray-900'>
                {locales.pricingFAQWatermark[globalSettings.lang]}
              </h3>
              <p className='mt-2 text-gray-600'>
                {locales.pricingFAQWatermarkAnswer[globalSettings.lang]}
              </p>
            </div>
            <div className='bg-white p-6 rounded-lg border border-gray-200'>
              <h3 className='text-lg font-medium text-gray-900'>
                {locales.pricingFAQPayment[globalSettings.lang]}
              </h3>
              <p className='mt-2 text-gray-600'>
                {locales.pricingFAQPaymentAnswer[globalSettings.lang]}
              </p>
            </div>
            <div className='bg-white p-6 rounded-lg border border-gray-200'>
              <h3 className='text-lg font-medium text-gray-900'>
                {locales.pricingFAQUpgradeDowngrade[globalSettings.lang]}
              </h3>
              <p className='mt-2 text-gray-600'>
                {locales.pricingFAQUpgradeDowngradeAnswer[globalSettings.lang]}
              </p>
            </div>
            <div className='bg-white p-6 rounded-lg border border-gray-200'>
              <h3 className='text-lg font-medium text-gray-900'>
                {locales.pricingFAQCancel[globalSettings.lang]}
              </h3>
              <p className='mt-2 text-gray-600'>
                {locales.pricingFAQCancelAnswer[globalSettings.lang]}
              </p>
            </div>
            <div className='bg-white p-6 rounded-lg border border-gray-200'>
              <h3 className='text-lg font-medium text-gray-900'>
                {locales.pricingFAQChangePayment[globalSettings.lang]}
              </h3>
              <p className='mt-2 text-gray-600'>
                {locales.pricingFAQChangePaymentAnswer[globalSettings.lang]}
              </p>
            </div>
          </div>
        </div>
      </div>
      <CTA
        subText={sprintf(locales.pricingCTASubtext[globalSettings.lang],plans[1].pricePerMonth)}
        globalSettings={globalSettings}
      />
      <Footer
        globalSettings={globalSettings}
      />
      {popupLevel !== null && (
        <div className='fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50'>
          <form
            onSubmit={handlePlanChange}
            className='bg-white rounded-xl shadow-lg max-w-md w-full mx-4 p-6 relative'
          >
            <h2 className='text-2xl font-bold text-center text-gray-900 mb-6'>
              {
                popupLevel<globalSettings.user?.next_level?
                sprintf(locales.pricingDowngradeTo[globalSettings.lang],plans[popupLevel].name):
                sprintf(locales.pricingUpgradeTo[globalSettings.lang],plans[popupLevel].name)
              }
            </h2>
            <div className='mb-6'>
              <ul className='space-y-3 text-sm text-gray-700'>
                {popupLevel >= globalSettings.user?.next_level ? (
                  <>
                    <li className='flex items-start'>
                      <span className='text-purple-800 mr-2'>•</span>
                      {locales.pricingPopupUpgradeImmediate[globalSettings.lang]}
                    </li>
                    <li className='flex items-start'>
                      <span className='text-purple-800 mr-2'>•</span>
                      {locales.pricingPopupProrated[globalSettings.lang]}
                    </li>
                    <li className='flex items-start'>
                      <span className='text-purple-800 mr-2'>•</span>
                      {locales.pricingPopupBilledImmediately[globalSettings.lang]}
                    </li>
                  </>
                ) : (
                  <>
                    <li className='flex items-start'>
                      <span className='text-purple-800 mr-2'>•</span>
                      {locales.pricingPopupDowngradeNextPeriod[globalSettings.lang]}
                    </li>
                    <li className='flex items-start'>
                      <span className='text-purple-800 mr-2'>•</span>
                      {locales.pricingPopupRemainCurrent[globalSettings.lang]}
                    </li>
                    <li className='flex items-start'>
                      <span className='text-purple-800 mr-2'>•</span>
                      {locales.pricingPopupNotBilled[globalSettings.lang]}
                    </li>
                  </>
                )}
              </ul>
            </div>
            <div className='flex space-x-4'>
              <button
                type='button'
                onClick={()=>{setPopupLevel(null);}}
                className='flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-lg font-medium hover:bg-gray-50 transition'
              >
                {locales.pricingCancel[globalSettings.lang]}
              </button>
              <button
                type='submit'
                className='flex-1 bg-purple-800 hover:bg-purple-600 text-white px-4 py-2 rounded-lg text-lg font-medium transition'
              >
                {locales.pricingConfirm[globalSettings.lang]}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
export default Pricing;

